import React, { useState, useMemo } from "react";
import axios from "axios";
import "../../Style/TableStyle.scss";
import "../../Style/__searchbar.scss";
import "../../Style/__button.scss";
import "./ExpencesTypePage.scss";
import {
  deleteAlert,
  errorAlert,
  handleSuccess,
  updateAlert,
} from "../../Components/Alerts";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { AiFillGolden } from "react-icons/ai";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReusableServerDataGrid from "../../Components/ReusableServerDataGrid";
import CrudPageHeader from "../../Components/CrudPageHeader";
import ExpencesTypeModal from "./ExpencesTypeModal";
import ExpencesTypeViewModal from "./ExpencesTypeViewModal";

export default function ExpenceTypePage() {
  const headers = authHeader();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedExpence, setSelectedExpence] = useState(null);
  const [viewExpence, setViewExpence] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [quickFilter, setQuickFilter] = useState([]);

  const getApiErrorMessage = (error, fallbackMessage) => {
    if (error?.response?.data?.errorMessage) {
      return `Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`;
    }
    if (
      typeof error?.response?.data === "string" &&
      error.response.data.trim() !== ""
    ) {
      return error.response.data;
    }
    if (error?.message) {
      return `Gabim: ${error.message}`;
    }
    return fallbackMessage;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        ApiLink.API_URL + "Accounts/accountByLedger",
        { headers, params: { ledger: 609 } },
      );
      setData(response.data);
    } catch (error) {
      errorAlert(
        getApiErrorMessage(
          error,
          "Gabim i panjohur në marrjen e klasave të shpenzimeve.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (item) => {
    setSelectedExpence(item);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedExpence(null);
  };

  const handleView = async (item) => {
    setViewExpence(item);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewExpence(null);
  };

  const handleDelete = async (deleteItemId) => {
    try {
      const response = await axios.delete(
        ApiLink.API_URL + "Accounts/expence" + `/${deleteItemId}`,
        { headers },
      );
      const result = response.data;
      if (result.success) {
        handleSuccess("Kategoria e shpenzimeve është fshirë me sukses");
        fetchData();
      } else {
        deleteAlert(
          "Kategoria e shpenzimeve nuk mund të fshihet sepse është përdorur në transaksione",
        );
      }
    } catch (error) {
      deleteAlert(
        "Ka ndodhur një gabim gjatë fshirjes së kategoris së shpenzimeve.",
      );
    }
  };

  const expenceGridHeader = [
    {
      field: "id",
      headerName: "#",
      width: 60,
      headerClassName: "super-app-theme--header",
      sortable: false,
      filterable: false,
      groupable: false,
      aggregable: false,
    },
    {
      field: "AccountName",
      headerName: "Emri i Klases",
      flex: 2.6,
      minWidth: 320,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "LedgerNameEN",
      headerName: "Llogaria Kontabël",
      flex: 1.2,
      minWidth: 200,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "IsActive",
      headerName: "Aktiv",
      width: 90,
      headerClassName: "super-app-theme--header",
      renderCell: (params) =>
        params.value ? (
          <CheckCircleOutlineIcon sx={{ color: "var(--cc-kpi-positive-text)" }} />
        ) : (
          ""
        ),
      filterType: "select",
      filterOptions: [
        { value: "true", label: "Aktiv" },
        { value: "false", label: "Joaktiv" },
      ],
    },
  ];

  const rows = data.map((item, index) => ({
    id: index + 1,
    AccountName: item.AccountName,
    LedgerNameEN: item.LedgerNameEN,
    IsActive: Boolean(item.IsActive),
    deleteItemId: item.AccountID,
    rowInfo: item,
  }));

  const filteredAndSortedRows = useMemo(() => {
    const searchText = quickFilter.join(" ").trim().toLowerCase();
    const activeFilters = filterModel?.items || [];

    const filtered = rows.filter((row) => {
      if (searchText) {
        const searchable = [row.AccountName, row.LedgerNameEN, row.IsActive ? "aktiv" : "joaktiv"]
          .join(" ")
          .toLowerCase();
        if (!searchable.includes(searchText)) return false;
      }

      return activeFilters.every((filter) => {
        const rawValue = row[filter.field];
        const expected = String(filter.value ?? "").toLowerCase();
        if (!expected) return true;
        if (filter.operator === "equals") {
          return String(rawValue).toLowerCase() === expected;
        }
        return String(rawValue ?? "").toLowerCase().includes(expected);
      });
    });

    const currentSort = sortModel?.[0];
    if (!currentSort?.field || !currentSort?.sort) return filtered;

    return [...filtered].sort((left, right) => {
      const comparison = String(left[currentSort.field] ?? "").localeCompare(
        String(right[currentSort.field] ?? ""),
        "sq",
        { numeric: true, sensitivity: "base" },
      );
      return currentSort.sort === "desc" ? -comparison : comparison;
    });
  }, [rows, filterModel, quickFilter, sortModel]);

  const paginatedRows = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    return filteredAndSortedRows.slice(start, start + paginationModel.pageSize);
  }, [filteredAndSortedRows, paginationModel]);

  React.useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="Crud__Container ETP__page">
      <ExpencesTypeModal
        show={showEditModal}
        onHide={handleCloseEditModal}
        mode="edit"
        initialData={selectedExpence}
        fetchData={fetchData}
      />

      <ExpencesTypeViewModal
        show={showViewModal}
        onHide={handleCloseViewModal}
        expenseData={viewExpence}
      />

      <CrudPageHeader
        icon={AiFillGolden}
        title="Klasifikimi i Shpenzimeve"
        description="Forma e Klasifikimeve: Menaxhoni, shtoni, editoni dhe fshini Klasa të shpënzimeve."
        tutorialUrl="https://www.youtube.com/@Accountree"
        tutorialLabel="Shiko Tutorialin"
        action={
          <ExpencesTypeModal
            fetchData={fetchData}
            type="text"
            className="button-68"
          />
        }
        className="ETP__header"
      />

      <ReusableServerDataGrid
        columns={expenceGridHeader}
        rows={paginatedRows}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onView={handleView}
        showViewAction={true}
        showPayment={false}
        actionsMinWidth={110}
        actionsFlex={0.8}
        height={500}
        rowCount={filteredAndSortedRows.length}
        loading={loading}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        sortModel={sortModel}
        setSortModel={setSortModel}
        filterModel={filterModel}
        setFilterModel={setFilterModel}
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
        getExportRows={() => Promise.resolve(filteredAndSortedRows)}
      />
    </div>
  );
}
